import React, { useEffect, useRef, useState } from 'react';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, Car as CarIcon, Image as ImageIcon } from 'lucide-react';
import { getSignedViewUrl, type SignedUrlInfo } from '@/lib/getSignedViewUrl';
import { supabase } from '@/lib/supabase';
import { useSettings } from '@/contexts/SettingsContext';
import type { Car } from '@/contexts/CarContext';
import { useCar } from '@/contexts/CarContext';
import { uploadCarImage } from '@/lib/uploadCarImage';
import { useI18n } from '@/i18n/LanguageContext';

interface CarHeroCarouselProps {
  car: Car;
  onEdit: () => void;
  onDelete: () => void;
}

const CarHeroCarousel: React.FC<CarHeroCarouselProps> = ({ car, onEdit, onDelete }) => {
  const { t } = useI18n();
  const [signedUrlInfo, setSignedUrlInfo] = useState<SignedUrlInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [latestOdometer, setLatestOdometer] = useState<number | null>(null);
  const { settings } = useSettings();
  const { updateCar } = useCar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!car.image_path) {
        setSignedUrlInfo(null);
        return;
      }
      try {
        setIsLoading(true);
        const info = await getSignedViewUrl(car.image_path);
        if (mounted) setSignedUrlInfo(info);
      } catch (e) {
        if (mounted) setSignedUrlInfo(null);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [car.image_path]);

  // Load latest odometer reading (use max reading as source of truth)
  useEffect(() => {
    let active = true;
    const fetchLatest = async () => {
      try {
        const { data } = await supabase
          .from('odometer_readings')
          .select('reading')
          .eq('car_id', car.id)
          .order('reading', { ascending: false })
          .order('recorded_at', { ascending: false })
          .limit(10); // Fetch top 10 to check for outliers
        
        if (!active) return;
        
        if (data && data.length > 0) {
          const { filterOdometerOutliers } = await import('@/lib/utils');
          const validReading = filterOdometerOutliers(data);
          setLatestOdometer(validReading);
        } else {
          setLatestOdometer(null);
        }
      } catch {
        if (active) setLatestOdometer(null);
      }
    };
    fetchLatest();
    return () => {
      active = false;
    };
  }, [car.id]);

  const handleTriggerChangePhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleChangePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      if (!accessToken || !userId) return;
      const imagePath = await uploadCarImage({ file, userId, entityId: car.id, type: 'car', accessToken });
      await updateCar(car.id, { image_path: imagePath });
      const info = await getSignedViewUrl(imagePath);
      setSignedUrlInfo(info);
    } catch (err) {
      // noop
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="w-full overflow-hidden rounded-[10px] bg-[#FDFDFD]" style={{ height: '30vh' }}>
      <Carousel opts={{ align: 'start', loop: false }} className="w-full h-full">
        <CarouselContent className="h-full">
          <CarouselItem className="h-full">
            <div className="relative w-full h-full">
              {/* Background image */}
              {signedUrlInfo?.url ? (
                <img
                  src={signedUrlInfo.url}
                  alt={`${car.nickname || car.brand} ${car.model}`}
                  className={`absolute inset-0 w-full h-full object-cover ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                  referrerPolicy="no-referrer"
                  crossOrigin="anonymous"
                  onLoadStart={() => setIsLoading(true)}
                  onLoad={() => setIsLoading(false)}
                  onError={() => setIsLoading(false)}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                  <CarIcon className="text-gray-400" size={36} />
                </div>
              )}

              {/* Gradient overlay for readability (top-focused) */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/10 to-transparent" />

              {/* Top-right actions */}
              <div className="absolute top-2 right-2 flex gap-2 z-20">
                <Button size="icon" className="h-8 w-8 p-0" onClick={handleTriggerChangePhoto} aria-label="Change photo">
                  <ImageIcon size={16} />
                </Button>
                <Button size="icon" className="h-8 w-8 p-0" onClick={onEdit} aria-label="Edit car">
                  <Edit size={16} />
                </Button>
                <Button size="icon" variant="destructive" className="h-8 w-8 p-0" onClick={onDelete} aria-label="Delete car">
                  <Trash2 size={16} />
                </Button>
              </div>
              <input type="file" ref={fileInputRef} onChange={handleChangePhoto} accept="image/*" className="hidden" />

              {/* Top-left overlays */}
              <div className="absolute left-3 top-3 z-10">
                <h1 className="text-white text-base font-medium">
                  {car.brand} {car.model}
                </h1>
                {car.licensePlate && (
                  <p className="text-white/90 text-base font-medium">{car.licensePlate}</p>
                )}
              </div>

              {/* Bottom row: Odometer + Fuel on the left, Health Report button on the right */}
              <div className="absolute left-3 right-3 bottom-2 z-10 flex items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2">
                  {latestOdometer != null ? (
                    <Badge className="bg-white/80 text-black hover:bg-white/90">
                      {settings?.distanceUnit === 'mi' ? Math.round(latestOdometer / 1.609344) : latestOdometer}{' '}
                      {settings?.distanceUnit || 'km'}
                    </Badge>
                  ) : car.mileage ? (
                    <Badge className="bg-white/80 text-black hover:bg-white/90">
                      {settings?.distanceUnit === 'mi' ? Math.round((car.mileage as number) / 1.609344) : car.mileage}{' '}
                      {settings?.distanceUnit || 'km'}
                    </Badge>
                  ) : null}
                  {car.fuelType ? (
                    <Badge className="bg-white/80 text-black hover:bg-white/90 capitalize">
                      {t(`car.fuelType.${car.fuelType}`) || car.fuelType}
                    </Badge>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Navigate to health report; kept simple, routing handled in parent via onClick wrappers
                    window.location.href = `/reports/health/${car.id}`;
                  }}
                  className="px-3 py-1.5 rounded-full text-black text-xs font-semibold shadow-sm hover:opacity-90"
                  style={{ backgroundColor: '#e7fe52' }}
                >
                  {t('healthReport.badge') || t('home.healthReport') || t('healthReport.title') || 'Health report'}
                </button>
              </div>
            </div>
          </CarouselItem>
        </CarouselContent>
      </Carousel>
    </div>
  );
};

export default CarHeroCarousel;


